import { ReactNode } from 'react';
import { motion } from 'framer-motion';
import { fadeUpVariant } from '@/lib/animations';
import { cn } from '@/lib/utils';

interface SectionProps {
  id?: string;
  eyebrow?: string;
  title: string | ReactNode;
  subtitle?: string | ReactNode;
  align?: 'left' | 'center';
  children: ReactNode;
  background?: 'default' | 'dark' | 'frost' | 'brand';
  className?: string;
}

export function Section({
  id,
  eyebrow,
  title,
  subtitle,
  align = 'center',
  children,
  background = 'default',
  className = '',
}: SectionProps) {
  const bgClasses = {
    default: 'bg-white',
    dark: 'bg-neutral-900 text-white',
    frost: 'bg-gradient-to-b from-snow-100 to-white',
    brand: 'bg-gradient-to-br from-brand-900 to-brand-700 text-white',
  };

  const alignClasses = align === 'center' ? 'text-center' : 'text-left';

  return (
    <section 
      id={id} 
      className={cn('py-20 md:py-32 relative overflow-hidden', bgClasses[background], className)}
    >
      {/* Subtle background pattern for frost/default */}
      {(background === 'frost' || background === 'default') && (
        <div className="absolute inset-0 opacity-[0.02] pointer-events-none">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#00000008_1px,transparent_1px),linear-gradient(to_bottom,#00000008_1px,transparent_1px)] bg-[size:3rem_3rem]" />
        </div>
      )}
      
      <div className="container-default mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={fadeUpVariant}
          className={alignClasses}
        >
          {eyebrow && (
            <motion.p 
              className="text-label text-accent-500 mb-6 tracking-widest"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              {eyebrow}
            </motion.p>
          )}
          <motion.h2 
            className="text-section-title mb-8"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            {title}
          </motion.h2>
          {subtitle && (
            <motion.div 
              className={cn('text-body-lg max-w-3xl', align === 'center' ? 'mx-auto' : '', background === 'default' || background === 'frost' ? 'text-neutral-700 font-medium' : 'text-snow-200')}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              {subtitle}
            </motion.div>
          )}
        </motion.div>
        {children}
      </div>
    </section>
  );
}

